/**
 * Windowing for long lists and tables: only the rows on screen (plus a margin) are in the
 * DOM, spacers stand in for the rest. Heights are fixed per row (the density decides
 * them), so offsets are exact and nothing jumps while scrolling.
 *
 * The list scrolls with its nearest scrolling ancestor (the page), so sticky headers and
 * the page's own scrollbar keep working.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';

/** Lists longer than this render windowed (DesignSystem 4.4). */
export const VIRTUALIZE_ABOVE = 100;

export interface VirtualWindow {
  /** First rendered index. */
  start: number;
  /** One past the last rendered index. */
  end: number;
  /** Space above the first rendered row, px. */
  padTop: number;
  /** Space below the last rendered row, px. */
  padBottom: number;
  /** Height of the whole list, px. */
  total: number;
}

/** offsets[i] is the top of row i; offsets[n] is the total height. */
export function prefixOffsets(heights: ArrayLike<number>): number[] {
  const out = new Array<number>(heights.length + 1);
  out[0] = 0;
  for (let i = 0; i < heights.length; i++) out[i + 1] = out[i]! + Math.max(0, heights[i] ?? 0);
  return out;
}

/** The row at vertical position y (clamped to the list). */
export function indexAt(offsets: number[], y: number): number {
  const n = offsets.length - 1;
  if (n <= 0) return 0;
  if (y <= 0) return 0;
  if (y >= offsets[n]!) return n - 1;
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (offsets[mid]! <= y) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/** Which rows to render for a viewport [top, bottom) measured from the list's top edge. */
export function computeWindow(offsets: number[], top: number, bottom: number, overscan: number): VirtualWindow {
  const n = offsets.length - 1;
  const total = offsets[n] ?? 0;
  if (n <= 0) return { start: 0, end: 0, padTop: 0, padBottom: 0, total: 0 };
  if (bottom <= 0 || top >= total) {
    // Off screen: render a small slice at the near end so the first paint has rows.
    const start = top >= total ? Math.max(0, n - overscan) : 0;
    const end = top >= total ? n : Math.min(n, overscan);
    return { start, end, padTop: offsets[start]!, padBottom: total - offsets[end]!, total };
  }
  const first = indexAt(offsets, Math.max(0, top));
  const last = indexAt(offsets, Math.max(0, bottom - 1));
  const start = Math.max(0, first - overscan);
  const end = Math.min(n, last + 1 + overscan);
  return { start, end, padTop: offsets[start]!, padBottom: total - offsets[end]!, total };
}

/** The nearest ancestor that scrolls vertically, or null for the document. */
export function scrollParent(el: HTMLElement | null): HTMLElement | null {
  let p = el?.parentElement ?? null;
  while (p) {
    const s = getComputedStyle(p);
    if (/(auto|scroll|overlay)/.test(s.overflowY)) return p;
    p = p.parentElement;
  }
  return null;
}

export interface UseVirtualOptions {
  count: number;
  /** Height of row i in px. Keep it stable (useCallback); change `heightKey` when heights change. */
  heightOf: (i: number) => number;
  /** Changes when any height changes (for example the density). */
  heightKey?: unknown;
  /** The list's own element (the first row sits at its top edge). */
  ref: RefObject<HTMLElement | null>;
  /** Default: on when count > VIRTUALIZE_ABOVE. */
  enabled?: boolean;
  overscan?: number;
}

export interface VirtualResult extends VirtualWindow {
  enabled: boolean;
  offsets: number[];
  /** Scroll the page so row i is in view (no-op when it already is). */
  scrollToIndex: (i: number, align?: 'nearest' | 'start') => void;
  /** How far the page has scrolled past the list's top edge (0 at or above it). */
  scrolledPast: number;
}

/** Before the first measure, assume a viewport this tall (CSS px) so the first paint is not empty. */
const FIRST_PAINT_PX = 1200;

/** Window a list against its scrolling ancestor. Renders everything when disabled. */
export function useVirtualRows({ count, heightOf, heightKey, ref, enabled, overscan = 10 }: UseVirtualOptions): VirtualResult {
  const on = enabled ?? count > VIRTUALIZE_ABOVE;
  const offsets = useMemo(
    () => prefixOffsets(Array.from({ length: count }, (_, i) => heightOf(i))),
    // heightKey stands for every input of heightOf.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [count, heightKey, heightOf],
  );
  const [view, setView] = useState<{ top: number; bottom: number }>({ top: 0, bottom: FIRST_PAINT_PX });
  const frame = useRef(0);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const sp = scrollParent(el);
    const rect = el.getBoundingClientRect();
    const viewTop = sp ? sp.getBoundingClientRect().top : 0;
    const viewH = sp ? sp.clientHeight : window.innerHeight;
    const top = viewTop - rect.top;
    const next = { top, bottom: top + viewH };
    setView((v) => (Math.abs(v.top - next.top) < 1 && Math.abs(v.bottom - next.bottom) < 1 ? v : next));
  }, [ref]);

  useLayoutEffect(() => {
    measure();
  }, [measure, count, heightKey]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const sp = scrollParent(el);
    const target: HTMLElement | Window = sp ?? window;
    const onScroll = () => {
      if (frame.current) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = 0;
        measure();
      });
    };
    target.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onScroll) : null;
    if (sp) ro?.observe(sp);
    ro?.observe(el);
    return () => {
      target.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      ro?.disconnect();
      if (frame.current) cancelAnimationFrame(frame.current);
      frame.current = 0;
    };
  }, [ref, measure]);

  const win = useMemo<VirtualWindow>(() => {
    if (!on) return { start: 0, end: count, padTop: 0, padBottom: 0, total: offsets[count] ?? 0 };
    return computeWindow(offsets, view.top, view.bottom, overscan);
  }, [on, count, offsets, view, overscan]);

  const scrollToIndex = useCallback(
    (i: number, align: 'nearest' | 'start' = 'nearest') => {
      const el = ref.current;
      if (!el || i < 0 || i >= count) return;
      const sp = scrollParent(el);
      const rowTop = offsets[i]!;
      const rowBottom = offsets[i + 1]!;
      const listTop = el.getBoundingClientRect().top - (sp ? sp.getBoundingClientRect().top : 0);
      const viewH = sp ? sp.clientHeight : window.innerHeight;
      // Leave room for a sticky header.
      const margin = 48;
      let delta = 0;
      if (align === 'start' || listTop + rowTop < margin) delta = listTop + rowTop - margin;
      else if (listTop + rowBottom > viewH) delta = listTop + rowBottom - viewH + 8;
      if (!delta) return;
      if (sp) sp.scrollTop += delta;
      else window.scrollBy({ top: delta });
    },
    [ref, count, offsets],
  );

  return { ...win, enabled: on, offsets, scrollToIndex, scrolledPast: Math.max(0, view.top) };
}
